import React from 'react';
import { Box, Flex, LoadingOrbit, Text } from '@chatsdet/ui';
import { CheckCircle, Circle, XCircle } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { useTestSignInActivityQuery } from './useTestSignIn';
import type { TestSignInGrant, TestSignInEvent } from './types';

function relativeTime(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

const EVENT_KIND_LABEL: Record<string, string> = {
  authorize_denied: 'Sign-in refused',
  token_denied: 'Token exchange refused',
  code_reused: 'Authorization code reused',
  provider_created: 'Provider set up',
  provider_updated: 'Provider updated',
  client_created: 'Application added',
  client_updated: 'Application updated',
  client_secret_rotated: 'Client secret rotated',
};

const REFUSAL_KINDS = new Set(['authorize_denied', 'token_denied', 'code_reused']);

/**
 * One step in a grant's checklist: Started → Authorized → Token issued, or
 * a terminal "Revoked" state overriding whatever else happened.
 */
function GrantChecklist({ grant }: { grant: TestSignInGrant }) {
  if (grant.revokedAt) {
    return (
      <Flex align="center" gap="xs">
        <XCircle size={14} className="text-destructive" weight="fill" />
        <Text size="xs" className="text-destructive">
          Revoked {relativeTime(grant.revokedAt)}
        </Text>
      </Flex>
    );
  }

  const steps: Array<{ label: string; done: boolean }> = [
    { label: 'Authorized', done: !!grant.authorizedAt },
    { label: 'Token issued', done: !!grant.tokenIssuedAt },
  ];

  return (
    <Flex align="center" gap="sm" wrap>
      {steps.map((step) => (
        <Flex key={step.label} align="center" gap="xs">
          {step.done ? (
            <CheckCircle size={14} className="text-success" weight="fill" />
          ) : (
            <Circle size={14} className="text-muted-foreground" />
          )}
          <Text size="xs" className={step.done ? 'text-foreground' : 'text-muted-foreground'}>
            {step.label}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

function EventRow({ event }: { event: TestSignInEvent }) {
  const isRefusal = REFUSAL_KINDS.has(event.kind);
  const detail =
    event.detail && typeof event.detail === 'object' && Object.keys(event.detail).length > 0
      ? event.detail
      : null;

  return (
    <Box className="py-2 border-b border-border last:border-b-0">
      <Flex align="start" justify="between" gap="sm">
        <Box className="min-w-0">
          <Flex align="center" gap="xs">
            <Text size="sm" weight="medium" className={isRefusal ? 'text-destructive' : undefined}>
              {EVENT_KIND_LABEL[event.kind] ?? event.kind}
            </Text>
          </Flex>
          <Text size="xs" className="text-muted-foreground mt-0.5">
            {event.message}
          </Text>
          {detail && (
            <pre className="mt-1.5 rounded bg-muted px-2 py-1.5 text-[11px] font-mono text-muted-foreground overflow-x-auto">
              {JSON.stringify(detail, null, 2)}
            </pre>
          )}
        </Box>
        <Text size="xs" className="text-muted-foreground shrink-0 whitespace-nowrap">
          {relativeTime(event.createdAt)}
        </Text>
      </Flex>
    </Box>
  );
}

type Props = {
  /** Scope activity to one credential (the credential dialog's diagnostics panel). Omit for org-wide. */
  credentialId?: string | null;
  limit?: number;
  /** Compact mode hides the section headings for use inside a smaller panel. */
  compact?: boolean;
};

export function TestSignInActivityPanel({ credentialId, limit = 25, compact = false }: Props) {
  const activityQuery = useTestSignInActivityQuery(credentialId, limit);

  if (activityQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-8">
        <LoadingOrbit size="sm" />
      </Flex>
    );
  }

  const grants = activityQuery.data?.data.grants ?? [];
  const events = activityQuery.data?.data.events ?? [];

  if (grants.length === 0 && events.length === 0) {
    return (
      <Text size="sm" className="text-muted-foreground">
        No sign-in activity yet.
      </Text>
    );
  }

  return (
    <Box className="space-y-4">
      {grants.length > 0 && (
        <Box>
          {!compact && (
            <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground mb-2">
              Recent sign-ins
            </Text>
          )}
          <Box className="space-y-2">
            {grants.map((grant) => (
              <Box key={grant.id} className="rounded-lg border border-border bg-card px-3 py-2">
                <Flex align="center" justify="between" gap="sm" wrap>
                  <Box className="min-w-0">
                    <Text size="sm" weight="medium" className="truncate">
                      {grant.email}
                    </Text>
                    <Text size="xs" className="text-muted-foreground">
                      Started {relativeTime(grant.startedAt)}
                    </Text>
                  </Box>
                  <GrantChecklist grant={grant} />
                </Flex>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {events.length > 0 && (
        <Box>
          {!compact && (
            <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground mb-2">
              Recent events
            </Text>
          )}
          <Box className="rounded-lg border border-border bg-card px-3">
            {events.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
